import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/authSlice";
import { SettingProvider, useSetting } from "../../controllers/SettingsContext";
import DashboardLayout from "../../components/DashboardLayout";
import { useCompanyLabelsControllers } from "../../controllers/useSettingsLabelsControllers";
import { ProfileManagement } from "../../components/ProfileManagement";
import { usePermission } from "../../hooks/usePermission";

export default function CompanySettings() {
    const user = useSelector(selectUser);

    const accountType: "company" | "individual" =
        user?.company || user?.account_type === "company" ? "company" : "individual";

    // pass accountType to the hook
    const { tabs, tabParam } = useCompanyLabelsControllers(accountType);

    const navigate = useNavigate();

    const handleTabClick = (value: string) => navigate(`?tab=${value}`);

    return (
        <DashboardLayout
            title="Settings"
            description="Define your identity and set up essential system configurations."
        >
            <SettingProvider accountType={accountType} user={user || undefined}>

                <div className="p-3 mt-8">
                    {/* tabs */}
                    <div className="flex overflow-x-auto mb-6 space-x-6 scrollbar-hide">
                        {tabs.map((tab: any) => (
                            <button
                                key={tab.value}
                                onClick={() => handleTabClick(tab.value)}
                                className={`pb-2 whitespace-nowrap ${tabParam === tab.value
                                        ? "border-b-2 border-blue-600 text-blue-600 font-semibold"
                                        : "text-[#1D1D1D] font-normal text-[14px] hover:text-gray-700"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* active section */}
                    <div className="bg-white rounded-lg shadow-sm pt-6 pb-6 pr-3 pl-3">
                        {tabParam === "profile" && (
                            <ProfileManagement type={accountType} category="profile" />
                        )}
                        {tabParam === "roles" && (
                            <ProfileManagement type={accountType} category="roles" />
                        )}
                        {tabParam === "permission" && (
                            <ProfileManagement type={accountType} category="permission" />
                        )}
                        {tabParam === "security" && (
                            <ProfileManagement type={accountType} category="security" />
                        )}
                        {tabParam === "notifications" && (
                            <ProfileManagement type={accountType} category="notifications" />
                        )}
                        {tabParam === "subscription" && (
                            <ProfileManagement type={accountType} category="subscription" />
                        )}
                    </div>

                    {tabParam === "profile" && <SaveFooter />}
                </div>
            </SettingProvider>
        </DashboardLayout>
    );
}

// save footer
const SaveFooter = () => {
    const { submitProfile, isSubmitting } = useSetting();
    const { hasPermission } = usePermission();

    return (
        <div className="flex justify-end gap-3 mt-6">
            <button className="px-4 py-2 rounded-md border">Cancel</button>
            <button
                onClick={submitProfile}
                disabled={isSubmitting || !hasPermission("update company profile")}
                className={`px-4 py-2 rounded-md text-white ${isSubmitting || !hasPermission("update company profile")
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-[#143887] hover:bg-blue-800"
                    }`}
            >
                {isSubmitting ? "Saving..." : "Save changes"}
            </button>
        </div>
    );
};
